import { NPC_KIN } from './kin.ts';
import { dayOf, earn, spend, message, type LifeEvent, type LifeState, money } from './life.ts';
import { localAmount, localPrice, hereCountry } from './character.ts';
import { Rng } from './rng.ts';

/**
 * Getting married, step by step: a ring (in your pocket), the question (in person, at the end of a date), an
 * engagement, the wedding planned (how big, when), and the day itself (you have to turn up). The guests' gifts come
 * on the day (at a Lagos wedding, the money sprayed on the dance floor). Miss your own wedding and it's off, and it
 * costs you dearly with them.
 */
const DAY = 1440;

export interface Ring { id: 'band' | 'gold' | 'diamond'; label: string; ngn: number; /** how much it helps the answer */ lift: number }
export const RINGS: Ring[] = [
  { id: 'band', label: 'A simple gold band', ngn: 150_000, lift: 0 },
  { id: 'gold', label: 'A gold ring with a small stone', ngn: 650_000, lift: 0.05 },
  { id: 'diamond', label: 'A diamond solitaire', ngn: 2_500_000, lift: 0.12 },
];
export type WeddingSize = 'small' | 'classic' | 'grand';
export const WEDDINGS: Record<WeddingSize, { label: string; note: string; ngn: number; guests: number; gifts: number }> = {
  small: { label: 'Small', note: 'The registry, then lunch with the families', ngn: 1_200_000, guests: 25, gifts: 350_000 },
  classic: { label: 'Classic', note: 'The ceremony and a reception, a hundred and twenty guests', ngn: 4_500_000, guests: 120, gifts: 1_900_000 },
  grand: { label: 'Grand', note: 'Everyone you’ve ever met: the hall, the band, the aso-ebi', ngn: 15_000_000, guests: 400, gifts: 7_500_000 },
};
export interface Engagement { name: string; ring: Ring['id']; since: number; wedding: { date: number; size: WeddingSize; cost: number; /** (a wedding between players) the vows, in real time (ms), and the wedding's id on the server; without them, an old one on a single life's clock */ at?: number; wid?: string } | null;
  /** engaged to a real player (their id): the date, the size and the hall are the same in both lives */
  player?: string;
  /** the hall, for a wedding between players (both go to the same one: its own room) */
  venue?: string;
  /** (between players) you've said "I do"; they have */
  vowed?: boolean; theyVowed?: boolean;
  /** (between players) their sex: husband or wife, after */
  theirSex?: 'm' | 'f' }
/** the guests' side: a wedding between players you're invited to */
export interface WeddingInvite { rid: string; from: string; couple: string; date: number; venue: string }

const rngFor = (s: LifeState, salt: number) => new Rng((s.seed ^ Math.imul(salt | 0, 0x9e3779b1)) >>> 0);
const partnerOf = (s: LifeState, name?: string) => s.contacts.find((c) => (name ? c.name === name : true) && ['Girlfriend', 'Boyfriend', 'Partner'].includes(c.relation));
/** the player you're engaged to (still a partner of yours) */
const playerFiance = (s: LifeState) => { const id = s.engaged?.player; return id ? (s.players ?? []).find((p) => p.id === id && ['Girlfriend', 'Boyfriend', 'Partner', 'Fiancée', 'Fiancé'].includes(p.relation ?? '')) : undefined; };

export const ringPrice = (s: LifeState, r: Ring) => localPrice(s, r.ngn);
export const weddingPrice = (s: LifeState, size: WeddingSize) => localPrice(s, WEDDINGS[size].ngn);

/** Buy the ring (it waits in your pocket for the right moment). */
export function buyRing(s: LifeState, id: Ring['id']): { ok: boolean; line: string } {
  const r = RINGS.find((x) => x.id === id)!;
  if (s.ring) return { ok: false, line: 'You already have a ring in your pocket.' };
  const price = ringPrice(s, r);
  if (!spend(s, price, `Engagement ring: ${r.label.toLowerCase()}`)) return { ok: false, line: `That one is ${money(s, price)}. You don’t have it.` };
  s.ring = id;
  const p = partnerOf(s);
  return { ok: true, line: `${r.label}, in a little box, in your pocket. ${p ? `Ask ${p.name.split(' ')[0]} at the end of your next date.` : 'Now you need someone to ask.'}` };
}

/** Whether you can ask them now (a partner, a ring, not engaged already). */
export const canPropose = (s: LifeState, name: string) => !!s.ring && !s.engaged && !!partnerOf(s, name);

/** The question, in person. `spark` is how the evening went (0..1). */
export function proposeTo(s: LifeState, name: string, spark: number): { yes: boolean; line: string; events: LifeEvent[] } {
  if (!NPC_KIN) return { yes: false, line: '…', events: [] }; // (NPC romance: archived, kin.ts)
  const p = partnerOf(s, name), ring = RINGS.find((x) => x.id === s.ring);
  if (!p || !ring || s.engaged) return { yes: false, line: '…', events: [] };
  const together = (s.minutes - (s.history.find((h) => h.text === `Started dating ${name}.`)?.t ?? p.met ?? s.minutes)) / DAY;
  const r = rngFor(s, s.minutes * 7 + 13);
  const chance = (p.closeness - 55) / 35 + (together > 60 ? 0.15 : together > 21 ? 0 : -0.25) + ring.lift + (spark - 0.5) * 0.3;
  const yes = r.chance(Math.max(0.05, Math.min(0.95, chance)));
  const first = name.split(' ')[0], events: LifeEvent[] = [];
  if (yes) {
    s.engaged = { name, ring: ring.id, since: s.minutes, wedding: null };
    s.ring = null;
    p.closeness = Math.min(100, p.closeness + 15);
    s.history.push({ t: s.minutes, text: `Engaged to ${name}.` });
    events.push({ t: s.minutes, kind: 'info', text: `You and ${first} are engaged. Plan the wedding in Contacts.` });
    const mum = s.contacts.find((c) => c.relation === 'Mother') ?? s.contacts.find((c) => c.relation === 'Father');
    if (mum) message(s, mum.name, hereCountry(s) === 'nigeria' ? `ENGAGED?! I’m calling your aunties. When is the introduction? When is the wedding?? 🙌🏾` : `Engaged! We’re so happy for you both. When’s the wedding?`, events);
  } else p.closeness = Math.max(0, p.closeness - 15);
  return { yes, events, line: yes ? r.pick([`"Yes! Yes, of course!" ${first} is crying and laughing at once.`, `"Are you serious? …Yes. YES."`, `"I was hoping you’d ask." ${first} holds out a hand for the ring.`]) : r.pick([`"I… I’m not ready. I’m sorry." The ring goes back in your pocket.`, `"Not yet. Ask me again when we’ve been together longer." You keep the ring.`]) };
}

/** Plan it: how big, how soon (on a Saturday, at eleven). The whole cost now. */
export function planWedding(s: LifeState, size: WeddingSize, weeks: 1 | 2 | 4): { ok: boolean; line: string } {
  const e = s.engaged;
  if (!e) return { ok: false, line: 'You’re not engaged.' };
  if (e.wedding) return { ok: false, line: 'The wedding is already planned.' };
  const cost = weddingPrice(s, size);
  if (!spend(s, cost, `Wedding (${WEDDINGS[size].label.toLowerCase()})`)) return { ok: false, line: `A ${WEDDINGS[size].label.toLowerCase()} wedding is ${money(s, cost)}. You don’t have it yet.` };
  let day = dayOf(s.minutes) + weeks * 7;
  while (day % 7 !== 5) day++; // (a Saturday)
  e.wedding = { date: day * DAY + 11 * 60, size, cost };
  s.messages.unshift({ t: s.minutes, from: e.name, text: `It’s happening!! ${WEDDINGS[size].label} wedding, Saturday at 11. Don’t you dare be late 💍`, read: false });
  return { ok: true, line: `Booked: a ${WEDDINGS[size].label.toLowerCase()} wedding, ${WEDDINGS[size].note.toLowerCase()}. Saturday, in ${weeks === 1 ? 'a week' : `${weeks} weeks`}, at 11:00.` };
}

/** It's the day (from 10 until the evening). */
export function weddingToday(s: LifeState, now = Date.now()): Engagement | null {
  const e = s.engaged;
  if (!e?.wedding || !(e.player ? playerFiance(s) : partnerOf(s, e.name))) return null;
  // (between players: real time, the same moment for both: the doors half an hour before the vows, two hours after.
  // One planned on a single life's clock (before) has no real time: it isn't on, till it's given one)
  if (e.player) return e.wedding.at && now >= e.wedding.at - 30 * 60_000 && now <= e.wedding.at + 2 * 3_600_000 ? e : null;
  const h = (s.minutes % DAY) / 60;
  return dayOf(s.minutes) === dayOf(e.wedding.date) && h >= 10 && h < 20 ? e : null;
}

/** The vows said: married. The guests' gifts. */
export function marry(s: LifeState): { line: string; gifts: number; events: LifeEvent[] } {
  const e = s.engaged, p = e && partnerOf(s, e.name);
  if (!e?.wedding || !p) return { line: '…', gifts: 0, events: [] };
  const w = WEDDINGS[e.wedding.size], r = rngFor(s, s.minutes + 77);
  const gifts = localAmount(s, Math.round(w.gifts * (0.8 + r.next() * 0.4)));
  p.relation = s.card.sex === 'm' ? 'Wife' : 'Husband';
  p.closeness = 100;
  s.history.push({ t: s.minutes, text: `Married ${e.name}.` });
  earn(s, gifts, hereCountry(s) === 'nigeria' ? 'Wedding: the money sprayed, and the envelopes' : 'Wedding gifts');
  s.needs.social = 100; s.needs.fun = Math.min(100, s.needs.fun + 40);
  s.engaged = null;
  const first = e.name.split(' ')[0];
  return { gifts, line: `You and ${first} are married. ${w.guests} guests, ${hereCountry(s) === 'nigeria' ? 'the band, the jollof, the money sprayed on the floor' : 'the speeches, the dancing, the cake'}: ${money(s, gifts)} in gifts.`, events: [{ t: s.minutes, kind: 'info', text: `You married ${first}.` }] };
}

/** Called off (from Contacts): the money spent on the wedding is gone. */
export function callOff(s: LifeState): string {
  const e = s.engaged; if (!e) return '…';
  const p = partnerOf(s, e.name); if (p) p.closeness = Math.max(0, p.closeness - 30);
  const pl = e.player ? (s.players ?? []).find((x) => x.id === e.player) : undefined;
  if (pl && (pl.relation === 'Fiancée' || pl.relation === 'Fiancé')) pl.relation = 'Partner';
  s.engaged = null;
  s.history.push({ t: s.minutes, text: `Called off the engagement to ${e.name}.` });
  return `It’s off. ${e.name.split(' ')[0]} takes the ring off and puts it on the table.`;
}

/** Daily: a wedding you never turned up to (it's off, and they won't forget); an engagement whose partner left. */
export function weddingDay(s: LifeState, events: LifeEvent[]) {
  const e = s.engaged;
  if (!e) return;
  // (a player: still together, or it's off; a missed day just isn't a wedding (they'll say so themselves))
  // (its time is real time: it's over when the world's clock says so. It was judged by this life's day against the day you
  // booked it, so the next game day, any game day, wiped the plan here while the server still had it, guests and all)
  if (e.player) { if (!playerFiance(s)) s.engaged = null; else if (e.wedding?.at && Date.now() > e.wedding.at + 2 * 3_600_000) { e.wedding = null; e.vowed = e.theyVowed = false; } return; }
  const p = partnerOf(s, e.name);
  if (!p) { s.engaged = null; return; }
  if (e.wedding && dayOf(s.minutes) > dayOf(e.wedding.date)) {
    p.closeness = Math.max(0, p.closeness - 45);
    e.wedding = null;
    message(s, e.name, 'You didn’t come. Everyone was there. EVERYONE. I waited at the front for an hour.', events);
    s.history.push({ t: s.minutes, text: `Didn’t turn up to the wedding with ${e.name}.` });
  }
}

// --- between two players ---------------------------------------------------------------------------------------

/** the ring in your pocket, and your partner (a player) here with you: you can ask */
export const canProposeTo = (s: LifeState, playerId: string) => !!s.ring && !s.engaged && ['Girlfriend', 'Boyfriend', 'Partner'].includes((s.players ?? []).find((p) => p.id === playerId)?.relation ?? '');
const fianceRole = (sex: 'm' | 'f') => (sex === 'm' ? 'Fiancé' : 'Fiancée');
/** engaged to a player (both lives, each its own side): `asker` you asked (your ring goes on their finger) */
export function engagePlayer(s: LifeState, pl: { id: string; name: string }, ring: Ring['id'], theirSex: 'm' | 'f', asker: boolean): string {
  const p = (s.players ?? []).find((x) => x.id === pl.id);
  if (p) p.relation = fianceRole(theirSex);
  s.engaged = { name: pl.name, player: pl.id, ring, since: s.minutes, wedding: null, theirSex };
  if (asker) s.ring = null;
  s.history.push({ t: s.minutes, text: `Engaged to ${pl.name}.` });
  return `You and ${pl.name.split(' ')[0]} are engaged. Plan the wedding together: Contacts → ${pl.name.split(' ')[0]}.`;
}
/** the hall's name for the two of you */
export const playerVenue = (s: LifeState, hall: string) => `${hall} (${s.card.name.split(' ')[0]} & ${(s.engaged?.name ?? '').split(' ')[0]})`;
/** can you pay for it (before the server's asked to book it) */
export function canAffordWedding(s: LifeState, size: WeddingSize): boolean { return !!s.engaged?.wedding?.cost || weddingPrice(s, size) <= s.money; }
/**
 * You plan it (and pay for it): at a real time you both share (`at`), in a hall of its own; the server has the record
 * (`wid`). One already paid for that was planned on a life's own clock (before) is given a real time, free.
 */
export function planPlayerWedding(s: LifeState, size: WeddingSize, at: number, wid: string, venue: string): { ok: boolean; line: string } {
  const e = s.engaged;
  if (!e?.player) return { ok: false, line: 'You’re not engaged to a player.' };
  const paid = e.wedding?.cost ?? 0;
  if (!paid) {
    const cost = weddingPrice(s, size);
    if (!spend(s, cost, `Wedding (${WEDDINGS[size].label.toLowerCase()})`)) return { ok: false, line: `A ${WEDDINGS[size].label.toLowerCase()} wedding is ${money(s, cost)}. You don’t have it yet.` };
    e.wedding = { date: s.minutes, size, cost, at, wid };
  } else e.wedding = { ...e.wedding!, at, wid };
  e.venue = venue;
  return { ok: true, line: paid ? 'The new time is booked: everyone you invited is told.' : `Booked: a ${WEDDINGS[size].label.toLowerCase()} wedding, ${WEDDINGS[size].note.toLowerCase()}.` };
}
/** they planned it: the same day and hall in your life (they paid) */
export function weddingPlanned(s: LifeState, plan: { at: number; wid: string; size: WeddingSize; venue: string }) {
  const e = s.engaged;
  if (!e?.player) return;
  e.wedding = { date: s.minutes, size: plan.size, cost: e.wedding?.cost ?? 0, at: plan.at, wid: plan.wid };
  e.venue = plan.venue;
}
/**
 * The server's word on your wedding (it keeps the record: this life only has its copy). A plan this life lost, or holds
 * under a wedding that's been replaced or called off, is put right from it; with no answer from the server nothing changes.
 * true: it changed.
 */
export function syncPlayerWedding(s: LifeState, list: { wid: string; at: number; until: number; size: WeddingSize; venue: string; status: string; by: string; me: string; couple: { id: string }[] }[], meId: string, now = Date.now()): boolean {
  const e = s.engaged;
  if (!e?.player) return false;
  const ours = list.filter((w) => w.me === 'couple' && w.couple.some((c) => c.id === e.player));
  const live = ours.filter((w) => w.status === 'planned' && now <= w.until).sort((a, b) => b.at - a.at)[0];
  const held = e.wedding?.wid ? ours.find((w) => w.wid === e.wedding!.wid) : null;
  if (live && (!e.wedding?.at || !held || held.status !== 'planned' || now > held.until)) {
    e.wedding = { date: s.minutes, size: live.size, cost: e.wedding?.cost || (live.by === meId ? weddingPrice(s, live.size) : 0), at: live.at, wid: live.wid };
    e.venue = live.venue;
    return true;
  }
  if (live && held && held.wid === live.wid && (e.wedding!.at !== live.at || e.venue !== live.venue)) { e.wedding = { ...e.wedding!, at: live.at }; e.venue = live.venue; return true; }
  if (!live && held?.status === 'cancelled') { e.wedding = null; e.vowed = e.theyVowed = false; return true; }
  return false;
}
/** both said "I do": married (each life its own side, its own share of the gifts) */
export function marryPlayer(s: LifeState, theirSex: 'm' | 'f'): { line: string; gifts: number; events: LifeEvent[] } {
  const e = s.engaged, p = e?.player ? (s.players ?? []).find((x) => x.id === e.player) : undefined;
  if (!e?.wedding || !p) return { line: '…', gifts: 0, events: [] };
  const w = WEDDINGS[e.wedding.size], r = rngFor(s, s.minutes + 79);
  const gifts = localAmount(s, Math.round((w.gifts / 2) * (0.8 + r.next() * 0.4)));
  p.relation = theirSex === 'm' ? 'Husband' : 'Wife';
  s.history.push({ t: s.minutes, text: `Married ${e.name}.` });
  earn(s, gifts, hereCountry(s) === 'nigeria' ? 'Wedding: the money sprayed, and the envelopes' : 'Wedding gifts');
  s.needs.social = 100; s.needs.fun = Math.min(100, s.needs.fun + 40);
  s.engaged = null;
  const first = e.name.split(' ')[0];
  return { gifts, line: `You and ${first} are married. ${hereCountry(s) === 'nigeria' ? 'The band, the jollof, the money sprayed on the floor' : 'The speeches, the dancing, the cake'}: ${money(s, gifts)} in gifts for you.`, events: [{ t: s.minutes, kind: 'info', text: `You married ${first}.` }] };
}
/** a wedding between players you're invited to, today (from 10 till 20) */
export function guestWeddingToday(s: LifeState): WeddingInvite | null {
  const list = (s as LifeState & { weddingInvites?: WeddingInvite[] }).weddingInvites ?? [], h = (s.minutes % DAY) / 60;
  return list.find((w) => dayOf(w.date) === dayOf(s.minutes) && h >= 10 && h < 20) ?? null;
}
export function addWeddingInvite(s: LifeState, w: WeddingInvite) {
  const k = s as LifeState & { weddingInvites?: WeddingInvite[] };
  k.weddingInvites = [...(k.weddingInvites ?? []).filter((x) => x.rid !== w.rid && dayOf(x.date) >= dayOf(s.minutes)), w];
}
